import { type JSX } from 'react';

import { useCachedData } from 'Components/Context/CachedDataContext';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { toTitleCase } from 'Helpers/StringUtils';
import { IssueHistoryModificationsResponse, IssueHistoryResponse } from 'Models/Issues';
import { OperationalControl } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

export interface ControlIssueHistoryTableProps {
    type: ProductModule.CONTROLS;
    histories: IssueHistoryResponse[];
    displayMappedControlsModal: (mappedControls: OperationalControl[]) => void;
    onSelectHistory: (history: IssueHistoryResponse) => void;
}

export interface DueDiligenceIssueHistoryTableProps {
    type: DueDiligenceModule;
    histories: IssueHistoryResponse[];
    getOrganizationName: (organizationId: string) => string;
    onSelectHistory: (history: IssueHistoryResponse) => void;
}

export type IssueHistoryTableProps = ControlIssueHistoryTableProps | DueDiligenceIssueHistoryTableProps;

export const IssueHistoryTable = (props: IssueHistoryTableProps): JSX.Element => {
    const cachedData = useCachedData();

    const columns: ColumnConfig<IssueHistoryResponse>[] = [
        {
            type: ColumnType.DATE,
            name: 'Date',
            getDateString: (history) => history.timestamp,
            getOnClickTextAction: () => ({ execute: props.onSelectHistory }),
        },
        {
            type: ColumnType.USER,
            name: 'Updated By',
            getUserId: (history) => history.issue.last_updated_by,
            users: cachedData.users,
        },
        {
            type: ColumnType.STRING,
            name: 'Status',
            getString: (history) => toTitleCase(history.issue.status),
        },
        {
            type: ColumnType.USER,
            name: 'Owner',
            getUserId: (history) => history.issue.owner_subject,
            users: cachedData.users,
        },
        props.type === ProductModule.CONTROLS
            ? {
                  type: ColumnType.MULTIPLE_CONTROLS,
                  name: 'Controls',
                  getControls: (history) => (history.issue as IssueHistoryModificationsResponse).impacted_controls,
                  onClick: props.displayMappedControlsModal,
              }
            : {
                  type: ColumnType.STRING,
                  name: props.type === ProductModule.UCRM ? 'Business' : 'Third Party',
                  getString: (history) => props.getOrganizationName(history.issue.impacted_vendor!),
              },
        {
            type: ColumnType.DATE,
            name: 'Due Date',
            getDateString: (history) => history.issue.due_date,
        },
    ];

    return <Table<IssueHistoryResponse> columns={columns} defaultSortColumn="Date" defaultSortDirection="descending" filters={[]} disableSorting getUniqueIdForRow={(history) => history.timestamp} rowsData={props.histories} />;
};
